import type z from 'zod';
import React from 'react';
import { Building, Building2, Earth, Hash, Mail, MapPin, Phone } from 'lucide-react';

import type { ReportSchema } from '@ballerine/common';
import { NOT_DETECTED } from '@ballerine/common';

import { ctw } from '@/common';
import { TextWithNAFallback } from '@/components/atoms';
import { filterValidStrings } from '@/components/templates/composites/MerchantDetailsContent/utils';
import { DataRow } from '@/components/templates/primitives';

type DFMerchantDetailsContentProps = {
  merchantDetails: NonNullable<z.infer<typeof ReportSchema>['data']>['merchantDetails'];
  className?: string;
};

export const DFMerchantDetailsContent: React.FC<DFMerchantDetailsContentProps> = ({
  merchantDetails,
  className,
}) => {
  return (
    <div className={ctw(className)}>
      <DataRow
        label="Company Name"
        Icon={Building}
        value={<TextWithNAFallback>{merchantDetails?.companyName}</TextWithNAFallback>}
        copyable
        copyValue={merchantDetails?.companyName || NOT_DETECTED}
        disableTranslation
      />

      <DataRow
        label="Alternative Company Names"
        Icon={Building2}
        value={(() => {
          const names = filterValidStrings(merchantDetails?.alternativeCompanyNames);

          if (!names.length) return null;

          return <span>{names.join(', ')}</span>;
        })()}
        copyable={!!filterValidStrings(merchantDetails?.alternativeCompanyNames).length}
        copyValue={
          filterValidStrings(merchantDetails?.alternativeCompanyNames).join(', ') || NOT_DETECTED
        }
        disableTranslation
      />

      <DataRow
        label="Registration Number"
        Icon={Hash}
        value={<TextWithNAFallback>{merchantDetails?.registrationNumber}</TextWithNAFallback>}
        copyable
        copyValue={merchantDetails?.registrationNumber || NOT_DETECTED}
      />

      <DataRow
        label="Phone Number"
        Icon={Phone}
        value={<TextWithNAFallback>{merchantDetails?.phoneNumber}</TextWithNAFallback>}
        copyable
        copyValue={merchantDetails?.phoneNumber || NOT_DETECTED}
      />

      <DataRow
        label="Email"
        Icon={Mail}
        value={<TextWithNAFallback>{merchantDetails?.email}</TextWithNAFallback>}
        copyable
        copyValue={merchantDetails?.email || NOT_DETECTED}
        disableTranslation
      />

      <DataRow
        label="Country"
        Icon={Earth}
        value={<TextWithNAFallback>{merchantDetails?.country}</TextWithNAFallback>}
        copyable
        copyValue={merchantDetails?.country || NOT_DETECTED}
      />

      <DataRow
        label="Address"
        Icon={MapPin}
        value={<TextWithNAFallback>{merchantDetails?.address}</TextWithNAFallback>}
        copyable
        copyValue={merchantDetails?.address || NOT_DETECTED}
      />
    </div>
  );
};
